/**
 * Shared rules for turning the hosted category configuration
 * (listing-categories.json) into links.
 *
 * Three places build brand and model URLs — the footer's brand shortlist, the
 * "alla märken" hub page and the model row on a brand page — and they have to
 * agree, or one page ends up with two live URLs competing to be its canonical.
 * The server-side sitemap follows the same two rules in
 * server/api-util/seoSitemap/seoSitemapUrls.js; it cannot import this file
 * because it is CommonJS and cannot reach into src/.
 */

/**
 * A category with no real name is a placeholder in the Console. The live config
 * has one: id "blank", with a single space for a name. It must never reach a URL
 * or a visible list.
 *
 * @param {Object} category category from the hosted configuration
 * @return {Boolean} true when the category is safe to show and link
 */
export const isUsableCategory = category =>
  !!category?.id && !!String(category.name || '').trim();

/**
 * The URL segment for a category: its id, lower-cased.
 *
 * Most ids are already lower-case but some are not ("Hyundai", "audi/A3",
 * "mazda/CX-5"). The footer has always built its brand links with
 * label.toLowerCase(), so /annonser/hyundai is the URL that exists in the wild;
 * emitting the raw id would add a second, equally self-canonical URL for the
 * same page. Lower-casing costs nothing at query time because SearchPage's
 * loadData matches the category case-insensitively and then queries with the
 * configuration's own spelling. No two ids collide once lower-cased, at either
 * level.
 *
 * @param {Object} category category from the hosted configuration
 * @return {String} the lower-cased id
 */
export const categorySlug = category => String(category.id).toLowerCase();

/**
 * @param {Array} categories categories from the hosted configuration
 * @return {Array} only the ones safe to show and link
 */
export const usableCategories = categories => (categories || []).filter(isUsableCategory);

/**
 * Find a category by the slug that appears in the URL. Case-insensitive, so a
 * visitor who types /annonser/VOLVO still lands on the right brand.
 *
 * @param {Array} categories categories from the hosted configuration
 * @param {String} slug URL segment
 * @return {Object|undefined} the matching category
 */
export const findCategoryBySlug = (categories, slug) => {
  const wanted = String(slug || '').toLowerCase();
  return wanted ? usableCategories(categories).find(c => categorySlug(c) === wanted) : undefined;
};

/**
 * @param {Object} category category from the hosted configuration
 * @return {Array} its linkable subcategories
 */
export const subcategoriesOf = category => usableCategories(category?.subcategories);
